import { addDays, format, getDay, isValid, parse } from 'date-fns';

/**
 * Dia útil BANCÁRIO brasileiro: segunda a sexta, excluídos os feriados nacionais em que
 * banco fecha. A régua é a do sistema bancário, não a do calendário civil — por isso
 * Carnaval (segunda e terça) entra mesmo sem ser feriado por lei federal.
 *
 * Serve ao "Vencimento em dias úteis" (ADR 0082): a data em que a fatura PODE ser paga
 * sem encargos quando o vencimento nominal cai em dia não útil (Lei 7.089/83). A data
 * derivada é condicional — juridicamente a dívida vence na data nominal (STJ, 2022) — e
 * por isso ela NUNCA é persistida: `invoices.due_date` guarda sempre a nominal, e esta
 * lib deriva a útil na leitura.
 *
 * Feriados calculados localmente (local-first, sem API): fixos + móveis derivados da
 * Páscoa (algoritmo de computus gregoriano). Feriados estaduais/municipais ficam de
 * fora de propósito — a compensação bancária nacional só fecha nos nacionais.
 */

/** Domingo de Páscoa do ano (computus gregoriano anônimo). */
function easterSunday(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = março, 4 = abril
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

const FIXED_HOLIDAYS_MMDD = [
    '01-01', // Confraternização Universal
    '04-21', // Tiradentes
    '05-01', // Dia do Trabalho
    '09-07', // Independência
    '10-12', // Nossa Senhora Aparecida
    '11-02', // Finados
    '11-15', // Proclamação da República
    '12-25', // Natal
    '12-31', // Último dia do ano: banco fecha (feriado bancário, como Carnaval)
];

const holidayCache = new Map<number, Set<string>>();

/** Feriados bancários nacionais do ano, como Set de 'yyyy-MM-dd'. */
export function getNationalBankHolidays(year: number): Set<string> {
    const cached = holidayCache.get(year);
    if (cached) return cached;

    const holidays = new Set<string>(FIXED_HOLIDAYS_MMDD.map((mmdd) => `${year}-${mmdd}`));

    // Consciência Negra só virou feriado nacional em 2024 (Lei 14.759/2023) — uma fatura
    // histórica de 20/11/2023 caiu em dia útil normal e não pode rolar retroativamente.
    if (year >= 2024) holidays.add(`${year}-11-20`);

    const easter = easterSunday(year);
    // Banco fecha na segunda e terça de Carnaval mesmo sem lei federal.
    holidays.add(format(addDays(easter, -48), 'yyyy-MM-dd')); // Segunda de Carnaval
    holidays.add(format(addDays(easter, -47), 'yyyy-MM-dd')); // Terça de Carnaval
    holidays.add(format(addDays(easter, -2), 'yyyy-MM-dd')); // Sexta-feira Santa
    holidays.add(format(addDays(easter, 60), 'yyyy-MM-dd')); // Corpus Christi

    holidayCache.set(year, holidays);
    return holidays;
}

/** O dia (yyyy-MM-dd) é útil bancário? Seg–sex fora dos feriados nacionais. */
export function isBankBusinessDay(dateStr: string): boolean {
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());
    // Data malformada conta como útil: devolver a string intacta em vez de estourar
    // RangeError no format() — effectiveDueDate roda dentro de render.
    if (!isValid(date)) return true;
    const weekday = getDay(date);
    if (weekday === 0 || weekday === 6) return false;
    return !getNationalBankHolidays(date.getFullYear()).has(dateStr);
}

/**
 * O próprio dia se já for útil; senão, o próximo útil (sempre pra frente — a convenção
 * de cartão nunca antecipa). Pior caso real: sábado de Carnaval → quarta-feira (+4).
 */
export function nextBankBusinessDay(dateStr: string): string {
    let date = parse(dateStr, 'yyyy-MM-dd', new Date());
    let result = dateStr;
    for (let i = 0; i < 14 && !isBankBusinessDay(result); i++) {
        date = addDays(date, 1);
        result = format(date, 'yyyy-MM-dd');
    }
    return result;
}

/**
 * Vencimento útil da fatura: a data nominal rolada pro próximo dia útil quando o cartão
 * tem "Vencimento em dias úteis" ligado; a própria nominal caso contrário. Uso EXCLUSIVO
 * nas superfícies de pagamento (exibição, lembrete, virada de "atrasada", fluxo de
 * caixa) — a competência e o agrupamento por mês seguem a nominal (ADR 0082).
 */
export function effectiveDueDate(
    dueDate: string,
    dueOnBusinessDay: number | boolean | null | undefined,
): string {
    if (!dueOnBusinessDay) return dueDate;
    return nextBankBusinessDay(dueDate);
}
